import { JSX, useContext, useEffect, useState } from "react";
import { PciDashboardSummaryType } from "@app/projects/network-assessment/pci-dashboard/shared";
import { getRoundingDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { ColumnConfig, printNumberAsString, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { resizeObserverManager } from "@app/shared/qes-standards/utility/ResizeObserverManager";
import { WafflesThemes } from "@app/shared/waffles/exports";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import { ClaimsActivityTopGhostProviderModel } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityTopGhostProviderModel";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";

interface ClaimsActivityTopGhostProviderTileProps {
  getTopGhostWidgetData: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  loading: boolean;
  onTopGhostDashboardDetailsViewClick: (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
  ) => void;
  summaryType: PciDashboardSummaryType;
  topGhostProvidersModel: ClaimsActivityTopGhostProviderModel;
}

interface ClaimsActivityTopGhostProviderTileToppings {
  description: string;
  ghostCount: string | JSX.Element;
  key: string;
  name: JSX.Element;
  percentage: string;
}

const ClaimsActivityTopGhostProviderTile = (props: ClaimsActivityTopGhostProviderTileProps): JSX.Element => {
  const [buildingRecords, setBuildingRecords] = useState<boolean>(false);
  const [loadAllClicked, setLoadAllClicked] = useState<boolean>(false);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);
  const [topGhostProviders, setTopGhostProviders] = useState<ClaimsActivityTopGhostProviderTileToppings[]>([]);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (!!props.topGhostProvidersModel?.records) {
      const waffleToppings: ClaimsActivityTopGhostProviderTileToppings[] = props.topGhostProvidersModel?.records.map((gs, index) => {
        /* oxlint-disable sort-keys */
        return {
          key: `${gs.name}-${index}`,
          name: (
            <EllipsisTooltip
              key={index}
              text={gs.name}
            />
          ),
          description: gs.description,
          ghostCount: (!!gs.ghostCount) ? (
            <QESButton
              onClick={() => props.onTopGhostDashboardDetailsViewClick(
                props.summaryType === PciDashboardSummaryType.County ? gs.name : gs.description,
                props.summaryType
              )}
              qesTestId={`claims-activity__top-ghost-provider-${props.summaryType.toLowerCase()}--dashboard-details-${index}`}
              qesType="secondary"
            >
              {printNumberAsString(gs.ghostCount, "---")}
            </QESButton>
          ) : printNumberAsString(gs.ghostCount, "---"),
          percentage: getRoundingDisplay(gs.percentage, true),
        };
        /* oxlint-enable sort-keys */
      });

      setTopGhostProviders(waffleToppings);
    }
  }, [props.topGhostProvidersModel, pciDashboardFiltersContext.displayedFilters]);


  useEffect(() => {
    const resizeCallback = (event: ResizeObserverEntry): void => {
      // when sorting or loading in a large amount of records,
      // there could be some time between when the api call is complete
      // and when the records are visibly displayed
      const resizeEntry = event?.target?.children;
      const tbodyElement = [...resizeEntry]?.find(x => x.localName === "tbody");

      if (tbodyElement?.childElementCount === topGhostProviders?.length && buildingRecords) {
        setLoadAllClicked(false);
        setBuildingRecords(false);
      }
    }

    const wafflesTable = document.querySelector(`[data-testid=claims-activity__top-ghost-provider-${props.summaryType.toLowerCase()}--table]`);

    if (!!wafflesTable) {
      resizeObserverManager.observe(wafflesTable, resizeCallback);

      return () => {
        resizeObserverManager.unobserve(wafflesTable);
      }
    }
  }, [topGhostProviders]);

  useEffect(() => {
    if (props.loading && !!topGhostProviders.length) {
      setTopGhostProviders([]);
    }
  }, [props.loading]);

  const columnConfig: ColumnConfig = {
    description: {
      displayText: props.summaryType === PciDashboardSummaryType.County ? "State" : "Code",
      testId: "description",
    },
    ghostCount: {
      displayText: "Count",
      testId: "ghost-count",
    },
    name: {
      displayText: props.summaryType,
      testId: "name",
    },
    percentage: {
      displayText: "Percent",
      testId: "ghost-percentage",
    },
  };

  const customSort = (
    records: ClaimsActivityTopGhostProviderTileToppings[],
    column: keyof ClaimsActivityTopGhostProviderTileToppings,
    sortedDirection: SortDirections
  ): ClaimsActivityTopGhostProviderTileToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);
    setBuildingRecords(true);

    props.getTopGhostWidgetData(null, props.summaryType, column, direction, true);

    return records;
  }

  const onLoadAllClick = () => {
    setLoadAllClicked(true);
    setBuildingRecords(true);
    props.getTopGhostWidgetData(null, props.summaryType, sortedColumn, sortDirection);
  }

  return (
    <div className="col-md-6 p0">
      <DashboardTile
        classNames="pci-dashboard__summary-tile px-2"
        loading={props.loading}
        title={`Ghost Providers by ${props.summaryType} - Network Providers`}
        subtitle={`As of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)}`}
        widthClass=""
      >
        <Waffles<ClaimsActivityTopGhostProviderTileToppings>
          columns={columnConfig}
          customSort={customSort}
          data={topGhostProviders}
          fullWidth={true}
          passThroughSorting={true}
          testId={`claims-activity__top-ghost-provider-${props.summaryType.toLowerCase()}--table`}
          theme={WafflesThemes.Scrollable}
        />

        {buildingRecords ? (
          <div className="d-flex justify-content-center mt-3">
            <LoadingSpinner />
          </div>
        ) : (
          (props.topGhostProvidersModel?.totalCount > topGhostProviders?.length && !loadAllClicked) && (
            <div className="d-flex justify-content-center mt-3">
              <QESButton
                qesTestId={`claims-activity__top-ghost-provider-${props.summaryType.toLowerCase()}--load-all`}
                qesType="primary"
                onClick={() => onLoadAllClick()}
              >
                Load All
              </QESButton>
            </div>
          )
        )}
      </DashboardTile>
    </div>
  )
}

export default ClaimsActivityTopGhostProviderTile;